/**
 * Short share links for storefront products.
 *
 * The links creators copy from their hub used to spell out every deep-link param,
 * so a paid DM ran to 101 characters:
 *
 *   /test100?open_messages=true&open_message_checkout=true&product_id=10&product_type=dm
 *
 * They are now written as a single token. Creators copy it as a path segment:
 *
 *   /test100/dm_10
 *
 * and the emails we send still carry it as the `p` param:
 *
 *   /test100?p=dm_10
 *
 * Why the path: link-preview robots (Instagram, Facebook, WhatsApp, iMessage)
 * only read the part of a link before the `?`. `/test100` is the hub's card, so a
 * `?p=` link previewed the hub photo whatever product it pointed at.
 * `/test100/<anything>` is handed to Django's
 * `get_SSR_data_content_page/<user>/<token>`, which answers with that product's
 * own title, description and image. Emails never render preview cards, so they
 * keep `?p=`.
 *
 * `expandShortProductLink()` turns either form back into the legacy params before
 * React mounts, so every existing reader (Influencer.js, Storefront.jsx,
 * InfluencerTabsBar.js, CoachingPreview.jsx, Community.js, ...) keeps reading the
 * params it always has. Nothing downstream needed changing, and the long
 * `?open_*` links creators have already pasted into bios and emails keep working
 * — this only changes what we *write*.
 *
 * Token grammar: `<kind>` on its own, or `<kind>_<id>`. A trailing `c` on the
 * kind means "open checkout", matching the linkType === 'checkout' branches in
 * the share-link builders.
 */

// Token -> the legacy params it expands to. `$` is replaced with the id.
const TOKEN_PARAMS = {
   sub:     { open_sub: 'true', single_tier: 'true', tier: '$' },
   subc:    { open_sub: 'true', single_tier: 'true', tier: '$', open_checkout: 'true' },
   mem:     { view_membership: 'true', id: '$' },
   comm:    { open_community: 'true', tab_id: '$' },
   commc:   { open_community_checkout: 'true', tab_id: '$' },
   course:  { open_collection: 'true', id: '$' },
   coursec: { open_buy_collection_checkout: 'true', open_collection: 'true', id: '$' },
   callt:   { open_coaching: 'true', tier: '$' },
   calltc:  { open_coaching: 'true', open_coaching_checkout: 'true', tier: '$' },
   dm:      { open_messages: 'true', open_message_checkout: 'true', product_id: '$', product_type: 'dm' },
   url:     { open_url_product: 'true', product_id: '$' },
   urlc:    { open_url_checkout: 'true', id: '$' },
   lead:    { open_lead_capture: 'true', id: '$' },
   dp:      { open_buy_now_checkout: 'true', id: '$' },
};

// The same kinds with no id — "open the section" rather than "open this product".
// `call` lives ONLY here: ?open_coaching=true opens the 1:1 coaching product,
// which has no per-item deep link (its readers honour open_coaching /
// open_coaching_checkout / tier and ignore any id). An id-bearing `call_<id>`
// would mint a param nothing reads, and coaching-call products deep-link via
// `url` instead — see the coachingCall branch in StorefrontProductList.js.
const BARE_TOKEN_PARAMS = {
   sub:    { open_sub: 'true' },
   mem:    { view_membership: 'true' },
   comm:   { open_community: 'true' },
   course: { open_collection: 'true' },
   call:   { open_coaching: 'true' },
   dm:     { open_messages: 'true' },
};

/**
 * Own-property lookup. A plain `TOKEN_PARAMS[kind]` also finds inherited members
 * like `constructor` and `toString`, which would make `?p=constructor_1` look
 * like a valid token and get silently consumed instead of left alone.
 */
function tokenParams(table, kind) {
   return Object.prototype.hasOwnProperty.call(table, kind) ? table[kind] : null;
}

export const SHORT_LINK_PARAM = 'p';

// First path segments that are app routes, not creator slugs. `/login/sub_4`,
// `/r/dm_7` and friends must reach their own routes untouched.
const RESERVED_FIRST_SEGMENTS = ['r', 'l', 'upload', 'edit', 'staff', 'affiliate', 'login'];
const RESERVED_FIRST_SEGMENT_PREFIX = 'become-a-creator';

const PATH_TOKEN_PATTERN = /^[a-z]+_\d+$/;
const TWO_SEGMENT_PATH_PATTERN = /^\/([^/]+)\/([^/]+)\/?$/;

/**
 * Builds the `p` token. Returns '' when the kind isn't one we can shorten, so
 * callers can fall back to their long-form URL.
 *
 * @param {string} kind  key of TOKEN_PARAMS / BARE_TOKEN_PARAMS
 * @param {string|number|null} [id]
 * @returns {string}
 */
export function shortProductToken(kind, id = null) {
   const key = String(kind || '').trim();
   if (!key) return '';

   const hasId = id !== null && id !== undefined && String(id).trim() !== '';
   if (!hasId) return tokenParams(BARE_TOKEN_PARAMS, key) ? key : '';
   if (!tokenParams(TOKEN_PARAMS, key)) return '';

   return `${key}_${String(id).trim()}`;
}

/**
 * Builds a full short share link, e.g. buildShortProductLink(origin, 'test100', 'dm', 10)
 * -> '<origin>/test100/dm_10'.
 *
 * A link to one product is a path, so preview robots (which ignore everything
 * after the `?`) get that product's own card. A section link with no id
 * ('mem', 'call', ...) stays `?p=<kind>` and previews the hub, which is what it
 * opens; so does an id that isn't a plain number.
 *
 * Returns '' when the kind can't be shortened, so the caller keeps its long URL.
 *
 * @returns {string}
 */
export function buildShortProductLink(origin, influencerSlug, kind, id = null) {
   const token = shortProductToken(kind, id);
   if (!token) return '';
   // origin/slug are interpolated even when blank, matching what the long-form
   // builders did, so a missing slug fails the same way it always has.
   const base = String(origin).replace(/\/+$/, '');
   // Only a numeric id can travel as a path: expandShortProductPath and Django's
   // preview route both stop at digits, and a dead path link is worse than a
   // `?p=` link that still opens the product.
   return PATH_TOKEN_PATTERN.test(token)
      ? `${base}/${influencerSlug}/${token}`
      : `${base}/${influencerSlug}?${SHORT_LINK_PARAM}=${token}`;
}

/**
 * Turns a `p` token into the legacy params. Returns null when the token is
 * missing or unrecognised.
 *
 * @param {string} token
 * @returns {Object|null}
 */
export function decodeShortProductToken(token) {
   const raw = String(token || '').trim();
   if (!raw) return null;

   const separator = raw.indexOf('_');
   const kind = separator === -1 ? raw : raw.slice(0, separator);
   const id = separator === -1 ? '' : raw.slice(separator + 1);

   if (!id) {
      const bare = tokenParams(BARE_TOKEN_PARAMS, kind);
      return bare ? { ...bare } : null;
   }

   const template = tokenParams(TOKEN_PARAMS, kind);
   if (!template) return null;

   const params = {};
   Object.keys(template).forEach((name) => {
      params[name] = template[name] === '$' ? id : template[name];
   });
   return params;
}

/**
 * Rewrites a query string, replacing the `p` token with the params it stands for.
 * Params already present win, so an explicit `?open_sub=true` is never clobbered,
 * and everything else on the URL (promo_code, aff, utm_*, ...) is preserved.
 *
 * Returns null when there is nothing to expand.
 *
 * @param {string} search  e.g. '?p=dm_10'
 * @returns {string|null}  e.g. 'open_messages=true&open_message_checkout=true&...'
 */
export function expandShortProductSearch(search) {
   let params;
   try {
      params = new URLSearchParams(search || '');
   } catch (e) {
      return null;
   }

   const token = params.get(SHORT_LINK_PARAM);
   if (!token) return null;

   const expanded = decodeShortProductToken(token);
   if (!expanded) return null;

   params.delete(SHORT_LINK_PARAM);
   Object.keys(expanded).forEach((name) => {
      if (!params.has(name)) params.set(name, expanded[name]);
   });

   return params.toString();
}

/**
 * Rewrites a path-form share link, `/<user>/<kind>_<id>`, to `/<user>` plus the
 * params the token stands for.
 *
 * Only a path of exactly two segments whose second segment decodes to a known
 * token with a numeric id is touched, so `/jane/12345` (a digital product),
 * `/jane/about` and the app's own routes (`/login/...`, `/r/...`) pass through.
 *
 * When the query already names a product (`open_*` or `view_membership`, as
 * affiliate links do) the token is only dropped from the path and nothing is
 * added, so those links behave exactly as they did before they gained a token.
 * Otherwise the params merge the way expandShortProductSearch merges them:
 * params already on the URL win, and everything else is preserved.
 *
 * @param {string} pathname  e.g. '/test100/dm_10'
 * @param {string} search    e.g. '?aff=CODE'
 * @returns {{pathname: string, search: string}|null}  null when nothing to rewrite
 */
export function expandShortProductPath(pathname, search) {
   const match = TWO_SEGMENT_PATH_PATTERN.exec(String(pathname || ''));
   if (!match) return null;

   const [, slug, segment] = match;
   const lowerSlug = slug.toLowerCase();
   if (RESERVED_FIRST_SEGMENTS.includes(lowerSlug)
      || lowerSlug.startsWith(RESERVED_FIRST_SEGMENT_PREFIX)) {
      return null;
   }

   if (!PATH_TOKEN_PATTERN.test(segment)) return null;
   const expanded = decodeShortProductToken(segment);
   if (!expanded) return null;

   const rawSearch = search || '';
   let params;
   try {
      params = new URLSearchParams(rawSearch);
   } catch (e) {
      return null;
   }

   const namesProduct = Array.from(params.keys())
      .some((name) => name.startsWith('open_') || name === 'view_membership');
   if (namesProduct) return { pathname: `/${slug}`, search: rawSearch };

   Object.keys(expanded).forEach((name) => {
      if (!params.has(name)) params.set(name, expanded[name]);
   });
   const query = params.toString();
   return { pathname: `/${slug}`, search: query ? `?${query}` : '' };
}

/**
 * Expands a short link in the address bar into the legacy params: both the
 * path form (`/test100/dm_10`) and the `?p=` form. A path token is unwrapped
 * first, then any `?p=` on what is left.
 *
 * Must run BEFORE React Router mounts — `replaceState` on its own doesn't update
 * the router's internal searchParams state, so calling this from a component
 * would leave the readers looking at the un-expanded URL. index.js calls it at
 * module load, ahead of root.render().
 *
 * @returns {boolean} whether the URL was rewritten
 */
export function expandShortProductLink() {
   if (typeof window === 'undefined' || !window.location) return false;

   try {
      const { pathname, search, hash } = window.location;

      const fromPath = expandShortProductPath(pathname, search);
      const nextPathname = fromPath ? fromPath.pathname : pathname;
      const nextSearch = fromPath ? fromPath.search : search;

      const expanded = expandShortProductSearch(nextSearch);
      if (!fromPath && expanded === null) return false;

      let query = nextSearch;
      if (expanded !== null) query = expanded ? `?${expanded}` : '';

      window.history.replaceState(
         window.history.state,
         '',
         `${nextPathname}${query}${hash}`
      );
      return true;
   } catch (e) {
      return false;
   }
}

export default buildShortProductLink;
